import React, { forwardRef } from 'react';
import { InputValidationConfig, validateAndSanitizeInput } from '../../utils/validation/inputValidation';
import InputCharacterCount from './InputCharacterCount';

interface ValidatedInputProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  validationConfig: InputValidationConfig;
  placeholder?: string;
  label?: string;
  className?: string;
  errorMessage?: string;
  isTextarea?: boolean;
  rows?: number;
  autoFocus?: boolean;
  disabled?: boolean;
  showCharacterCount?: boolean;
}

/**
 * A validated input component that handles character limits and validation
 */
const ValidatedInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, ValidatedInputProps>(({
  id,
  name,
  value,
  onChange,
  onBlur,
  validationConfig,
  placeholder,
  label,
  className = '',
  errorMessage,
  isTextarea = false,
  rows = 4,
  autoFocus = false,
  disabled = false,
  showCharacterCount = true,
  onKeyDown,
}, ref) => { // Accept the ref here
  // Sanitize the input value based on validation config
  const sanitizedValue = validateAndSanitizeInput(value, validationConfig);
  
  // Handle blur event to sanitize input
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    // If the value has been truncated, update the input
    if (value !== sanitizedValue) {
      const newEvent = {
        ...e,
        target: {
          ...e.target,
          value: sanitizedValue,
        },
      } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
      
      onChange(newEvent);
    }
    
    // Call the provided onBlur handler if it exists
    if (onBlur) {
      onBlur(e);
    }
  };
  
  // Common input props
  const inputProps = {
    id,
    name,
    value,
    onChange,
    onBlur: handleBlur,
    onKeyDown,
    placeholder,
    disabled,
    autoFocus,
    className: `w-full px-3 py-2 border ${
      errorMessage
        ? 'border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500'
        : 'border-gray-300 dark:border-gray-700 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500'
    } rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out ${className}`,
    maxLength: validationConfig.maxLength,
  };
  
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      
      {isTextarea ? (
        <textarea ref={ref as React.Ref<HTMLTextAreaElement>} {...inputProps} rows={rows} />
      ) : (
        <input type="text" ref={ref as React.Ref<HTMLInputElement>} {...inputProps} />
      )}
      
      {showCharacterCount && (
        <InputCharacterCount
          currentLength={value.length}
          maxLength={validationConfig.maxLength}
        />
      )}
      
      {errorMessage && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errorMessage}</p>
      )}
    </div>
  );
});

export default ValidatedInput;
